<template>
  <div class="scooter-section">
    <VideoHeroContent
      class="scooter-section__video"
      :video="content.teaser.video"
      :row-reverse="true"
      hero-image-align="left"
      video-aria-label="Informationsvideo für den Xiaomi Scooter"
    >
      <CdnImage
        slot="hero"
        class="scooter-section__teaser-hero"
        :src="content.teaser.hero.src"
        :alt="content.teaser.hero.alt"
        lazy
      />
      <Heading
        tag="h2"
        size="lg"
        text-uppercase
        font-weight="ultra"
      >
        {{ content.teaser.title }}
      </Heading>
      <Paragraph>
        {{ content.teaser.copy }}
      </Paragraph>
      <div class="scooter-section__teaser-buttons">
        <LinkButton
          tag="nuxt-link"
          :to="content.teaser.cta.target"
          primary
        >
          {{ content.teaser.cta.label }}
        </LinkButton>
        <LinkButton
          tag="a"
          transparent
          to="#comparison"
        >
          Features vergleichen
        </LinkButton>
      </div>
    </VideoHeroContent>
    <GridContainer padding>
      <ComparisonTable
        id="comparison"
        :data="enrichedComparisonTableData"
        :heading-title="content.comparisonTable.title"
        :sub-title="content.comparisonTable.copy"
        class="scooter-section__comparison"
      >
        <template #default="slotProps">
          <ComparisonProduct
            :title="slotProps.data?.title"
            :products="slotProps.data?.products"
            :products-big="slotProps.data?.productsBig"
            :has-same-height="slotProps.sameHeight"
            class="scooter-section__comparison-product"
          >
            <template #image="{ itemCurrent: currentImage }">
              <nuxt-link
                v-if="currentImage.slug"
                :to="currentImage.slug"
                :title="currentImage.alt"
              >
                <CdnImage
                  :alt="currentImage.alt"
                  :src="currentImage.src"
                  lazy
                />
              </nuxt-link>
              <CdnImage
                v-else
                :alt="currentImage.alt"
                :src="currentImage.src"
                lazy
              />
            </template>
          </ComparisonProduct>
          <ContentToggle :show-content="slotProps.contentToggle">
            <ComparisonItemValues
              v-for="(val, index) in slotProps.data?.values"
              :key="index"
              :title="val.title"
              :content="val.content"
              :list="val.list"
              :additional-info="val.info"
              :is-checked="val.checked"
            />
            <div class="scooter-section__comparison-buttons">
              <AddToCartButtonContainer
                :offer="slotProps.data?.offer"
                :display-icon="false"
                :disabled="!slotProps.data?.offer"
              />

              <LinkButton
                :to="`/geraete/${slotProps.data?.offer?.product?.slug}`"
                :tag="slotProps.data?.offer?.product?.slug ? 'nuxt-link' : 'button'"
                transparent
                :disabled="!slotProps.data?.offer?.product?.slug"
              >
                Mehr erfahren
              </LinkButton>
            </div>
            <Paragraph
              v-if="
                !slotProps.data?.offer ||
                (slotProps.data?.offer?.product?.availability?.stock || 0) < 1
              "
              class="error-message"
              >In Kürze verfügbar.</Paragraph
            >
          </ContentToggle>
        </template>
      </ComparisonTable>
    </GridContainer>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import {
  GridContainer,
  VideoHeroContent,
  Heading,
  Paragraph,
  LinkButton,
  ComparisonTable,
  ComparisonProduct,
  ComparisonItemValues,
  ContentToggle,
} from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';

type ScooterOffer = {
  articleNumber?: string;
  offer?: any;
  tileGroups?: string[];
};

const props = defineProps({
  offers: {
    type: Array as PropType<ScooterOffer[]>,
    default: () => [],
  },
});

const content = {
  teaser: {
    video: {
      src: '/content/content-videos/landingpages/xiaomi/6fd6f5e42732ff8bea33e71e83744196/Xiaomi-Scooter-Clip.mp4',
      poster:
        '/_ipx/f_webp,w_640/content/content-images/landingpages/xiaomi/scooters/7f04928bef58b07bd1b4a8a66c31dd05/video-fallback.jpg',
    },
    title: 'Xiaomi Scooter: Outdoor Lifestyle',
    copy: 'Entdecke die Welt der Xiaomi Scooter und erlebe Mobilität in einem neuen Licht. Unsere Elektroroller bieten eine einzigartige Kombination aus Stil, Fahrkomfort und Leistung. Für mehr Sicherheit und Kontrolle verfügen unsere Scooter über intuitive Bedienelemente und eine Interkonnektivität mit anderen Xiaomi Home Appliances.',
    cta: {
      label: 'Xiaomi Scooter entdecken',
      target: '/zubehoer/_xiaomi?filters[]=scooter',
    },
    hero: {
      src: '/content/content-images/landingpages/xiaomi/scooters/3825fc98f2ca8603c47c9602dec6c24c/vergleichstabelle-scooter-4-ultra@2x.png',
      alt: 'Xiaomi Scooter 4 Ultra',
    },
  },
  comparisonTable: {
    title: 'Finde Deinen perfekten Scooter',
    copy: 'Vergleiche die Top-Features unserer Electric Scooter',
    items: [
      {
        title: 'Xiaomi Electric Scooter 4 Lite (2nd Gen)',
        articleNumber: '6941812765388',
        products: [
          {
            alt: 'Xiaomi Electric Scooter 4 Lite (2nd Gen)',
            src: '/content/content-images/landingpages/xiaomi/scooters/66116aec1849287dc4a7b3eeb6824930/vergleichstabelle-scooter-4-lite@2x.png',
            slug: '/zubehoer/_xiaomi?filters[]=scooter',
          },
        ],
        values: [
          {
            title: 'Nennleistung',
            content: '300 W',
            checked: false,
          },
          {
            title: 'Steigungswinkel:',
            content: 'bis zu 15%',
            checked: false,
          },
          {
            title: 'Max. Geschwindigkeit:',
            content: '20km/h',
            checked: false,
          },
          {
            title: 'Reichweite:',
            content: 'bis zu 25km',
            checked: false,
          },
          {
            title: 'Bereifung:',
            content: '10" Luftreifen',
            checked: false,
          },
          {
            title: 'Max. Belastung:',
            content: '100kg',
            checked: false,
          },
          {
            title: 'Gewicht:',
            content: '15,4kg',
            checked: false,
          },
          {
            title: 'Abmessungen:',
            content: '1141 x 440 x 1224mm',
            checked: false,
          },
          {
            title: 'Akkuleistung:',
            content: '10400mAh 225Wh',
            checked: false,
          },
          {
            title: 'Bildschirm:',
            content: 'LED-Anzeige mit Geschwindigkeit/ Akku/ Status/ Modus',
            checked: false,
          },
          {
            title: 'Kompatibel mit:',
            content: 'Android 4.3 oder iOS 9.0 und höher',
            checked: false,
          },
          {
            title: 'Drahtlose Konnektivität:',
            content: 'Bluetooth® 4.1 BLE und höher',
            checked: false,
          },
          {
            title: 'Federung (vorne/hinten):',
            content: '',
            checked: false,
          },
          {
            title: 'Blinker (rechts und links am Lenker):',
            content: '',
            checked: true,
          },
          {
            title: 'Straßenzulassung:',
            content: '',
            checked: true,
          },
          {
            title: 'Faltbar in 3 Sekunden:',
            content: '',
            checked: true,
          },
          {
            title: 'Energierückgewinnung (Kers):',
            content: '',
            checked: true,
          },
          {
            title: 'Schlauchlos mit Notlaufeigenschaften:',
            content: '',
            checked: false,
          },
        ],
      },
      {
        title: 'Xiaomi Electric Scooter 4 Pro (2nd Gen)',
        articleNumber: '6941812765791',
        products: [
          {
            alt: 'Xiaomi Electric Scooter 4 Pro (2nd Gen)',
            src: '/content/content-images/landingpages/xiaomi/scooters/c9d4b8c9f934dd77dd98ff66e9902ffe/vergleichstabelle-scooter-4-pro@2x.png',
            slug: '/geraete/xiaomi-electricscooter4-pro-2nd-gen-reichweite-bis-60-km-schwarz',
          },
        ],
        values: [
          {
            title: 'Nennleistung',
            content: '400 W',
            checked: false,
          },
          {
            title: 'Steigungswinkel:',
            content: 'bis zu 20%',
            checked: false,
          },
          {
            title: 'Max. Geschwindigkeit:',
            content: '20km/h',
            checked: false,
          },
          {
            title: 'Reichweite:',
            content: 'bis zu 60km',
            checked: false,
          },
          {
            title: 'Bereifung:',
            content: '10" Schlauchlos-Reifen',
            checked: false,
          },
          {
            title: 'Max. Belastung:',
            content: '120kg',
            checked: false,
          },
          {
            title: 'Gewicht:',
            content: '19kg',
            checked: false,
          },
          {
            title: 'Abmessungen:',
            content: '1150 x 570 x 1215mm',
            checked: false,
          },
          {
            title: 'Akkuleistung:',
            content: '10000mAh 468Wh',
            checked: false,
          },
          {
            title: 'Bildschirm:',
            content: 'LED-Anzeige mit Geschwindigkeit/ Akku/ Status/ Modus',
            checked: false,
          },
          {
            title: 'Kompatibel mit:',
            content: 'Android 4.3 oder iOS 9.0 und höher',
            checked: false,
          },
          {
            title: 'Drahtlose Konnektivität:',
            content: 'Bluetooth® 4.1 BLE und höher',
            checked: false,
          },
          {
            title: 'Federung (vorne/hinten):',
            content: '',
            checked: false,
          },
          {
            title: 'Blinker (rechts und links am Lenker):',
            content: '',
            checked: true,
          },
          {
            title: 'Straßenzulassung:',
            content: '',
            checked: true,
          },
          {
            title: 'Faltbar in 3 Sekunden:',
            content: '',
            checked: true,
          },
          {
            title: 'Energierückgewinnung (Kers):',
            content: '',
            checked: true,
          },
          {
            title: 'Schlauchlos mit Notlaufeigenschaften:',
            content: '',
            checked: true,
          },
        ],
      },
      {
        title: 'Xiaomi Electric Scooter 4 Ultra',
        articleNumber: '6934177787782',
        products: [
          {
            alt: 'Xiaomi Electric Scooter 4 Ultra',
            src: '/content/content-images/landingpages/xiaomi/scooters/3825fc98f2ca8603c47c9602dec6c24c/vergleichstabelle-scooter-4-ultra@2x.png',
            slug: '/zubehoer/_xiaomi?filters[]=scooter',
          },
        ],
        values: [
          {
            title: 'Nennleistung',
            content: '500 W',
            checked: false,
          },
          {
            title: 'Steigungswinkel:',
            content: 'bis zu 25 %',
            checked: false,
          },
          {
            title: 'Max. Geschwindigkeit:',
            content: '20km/h',
            checked: false,
          },
          {
            title: 'Reichweite:',
            content: 'bis zu 70km',
            checked: false,
          },
          {
            title: 'Bereifung:',
            content: '10" Xiaomi DuraGel',
            checked: false,
          },
          {
            title: 'Max. Belastung:',
            content: '120kg',
            checked: false,
          },
          {
            title: 'Gewicht:',
            content: '25kg',
            checked: false,
          },
          {
            title: 'Abmessungen:',
            content: '1200 x 550 x 1260mm',
            checked: false,
          },
          {
            title: 'Akkuleistung:',
            content: '12000mAh 561,5Wh',
            checked: false,
          },
          {
            title: 'Bildschirm:',
            content: 'LED-Anzeige mit Geschwindigkeit/ Akku/ Status/ Modus',
            checked: false,
          },
          {
            title: 'Kompatibel mit:',
            content: 'Android 4.3 oder iOS 9.0 und höher',
            checked: false,
          },
          {
            title: 'Drahtlose Konnektivität:',
            content: 'Bluetooth® 4.1 BLE und höher',
            checked: false,
          },
          {
            title: 'Federung (vorne/hinten):',
            content: '',
            checked: true,
          },
          {
            title: 'Blinker (rechts und links am Lenker):',
            content: '',
            checked: true,
          },
          {
            title: 'Straßenzulassung:',
            content: '',
            checked: true,
          },
          {
            title: 'Faltbar in 3 Sekunden:',
            content: '',
            checked: true,
          },
          {
            title: 'Energierückgewinnung (Kers):',
            content: '',
            checked: true,
          },
          {
            title: 'Schlauchlos mit Notlaufeigenschaften:',
            content: '',
            checked: true,
          },
        ],
      },
    ],
  },
};

const enrichedComparisonTableData = computed(() =>
  content.comparisonTable.items.map((item) => {
    const offersCorrespondant = props.offers.find(
      (offer) => offer.articleNumber === item.articleNumber
    );
    if (offersCorrespondant) return { ...item, ...offersCorrespondant };
    return item;
  })
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.scooter-section {
  :deep(.video-hero-content__copy) {
    @include mq($mq-sm) {
      @include font-size(21px, 24px);
    }
  }

  :deep(.video-hero-content__hero-image) {
    left: -36px;

    @include mq($mq-md) {
      left: -76px;
    }
  }

  &__video {
    padding-bottom: 80px;

    @include mq($mq-md) {
      padding-bottom: 120px;
    }

    :deep(.video-hero-content__hero-image) {
      width: 60%;

      @include mq($mq-md) {
        width: 65%;
      }

      @include mq($mq-xl) {
        width: 640px;
      }

      img {
        aspect-ratio: 1;
        width: 100%;
      }
    }

    :deep(.video-player) {
      --play-button-position-left: calc(100% - 50px);
    }
  }

  &__teaser {
    &-buttons {
      display: flex;
      flex-direction: column;
      gap: $grid-base;
    }
  }

  &__comparison {
    &-product {
      // comparison table has no image dimensions set:
      :deep(.comparison-product__image) img {
        width: 100%;
        aspect-ratio: 1;
        display: block;
      }
    }

    &-buttons {
      display: flex;
      flex-direction: column;
      gap: $grid-base;
      max-width: fit-content;
      margin-bottom: $grid-base;

      @include mq($mq-lg) {
        flex-direction: row;
      }
    }
  }

  :deep(.comparison-product__title) {
    height: 62px;
  }
}
</style>
